import SideBarMenuBlock from './SideBarMenuBlock/SideBarMenuBlock'
import {
  Box,
  Center,
  CloseButton,
  Image,
  NavLink as MantineNavLink,
  ScrollArea,
  Stack,
} from '@mantine/core'
import { NavLink } from 'react-router-dom'
import { useAuthStore } from '@store/auth.store'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'
import {
  filterDevicesDueTo30DaysReport,
  filterDevicesWithValveErrors,
} from '@common/utils/helperFunctions'
import { useVisibleDevices } from '@data/device/device'
import { type ReactNode } from 'react'
import {
  FaBuilding,
  FaCloud,
  FaCog,
  FaDollarSign,
  FaFaucet,
  FaFlask,
  FaHandsHelping,
  FaHistory,
  FaMoneyCheck,
  FaTachometerAlt,
  FaWater,
} from 'react-icons/fa'
import {
  FaRss,
  FaScrewdriverWrench,
  FaTriangleExclamation,
  FaUsers,
} from 'react-icons/fa6'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { UserAccountAssoc } from '@data/users/model/user'

import styles from './SideBar.module.scss'

export interface IMenuItems {
  href: string
  title: string
  icon: ReactNode
  permissions: string[]
  defaultShow: boolean
  active?: boolean
  tab?: string
}

interface INavItems {
  heading: string
  menuItems: IMenuItems[]
}

type SideBarProps = {
  onNavigate: () => void
}

function SideBar({ onNavigate }: SideBarProps) {
  const permissions = useAuthStore((state) => state.permissions)
  const { data: userInfo } = useCurrentUserQuery()
  const devices = useVisibleDevices()

  const devicesDueTo30DaysReport = filterDevicesDueTo30DaysReport(devices)
  // Same device pool the Operate Valves page itself shows — see ValvesManagement.tsx.
  const valveDevicesWithErrors = filterDevicesWithValveErrors(
    devices.filter((device) => device.model === '3V004'),
  )

  const menu: INavItems[] = [
    {
      heading: 'Dashboard',
      menuItems: [
        {
          href: '/home',
          title: 'Dashboard',
          icon: <FaTachometerAlt />,
          permissions: [],
          defaultShow: false,
        },
        {
          href: '/hourly-usage',
          title: 'Location Performance',
          icon: <FaTachometerAlt />,
          permissions: [],
          defaultShow: false,
        },
        {
          href: '/device-status',
          title: 'Device Status',
          icon: <FaRss />,
          permissions: ['READ:DEVICE:PROD', 'READ:DEVICE:STATUS:PROD'],
          defaultShow: false,
        },
        {
          href: '/total-spent',
          title: 'Total Spent',
          icon: <FaDollarSign />,
          permissions: ['READ:USAGE:COST'],
          defaultShow: false,
        },
        {
          href: '/device-co2',
          title: 'Total Co2 Produced',
          icon: <FaCloud />,
          permissions: ['READ:REPORTS:CO2'],
          defaultShow: false,
        },
        {
          href: '/alert-queue',
          title: 'Alert Queue',
          icon: <FaTriangleExclamation />,
          permissions: ['READ:ALERTS:USAGE'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Reporting',
      menuItems: [
        {
          href: '/30days',
          title: '30 Days Report',
          icon: <FaWater />,
          permissions: ['READ:REPORTS:30DAY'],
          defaultShow: false,
        },
        {
          href: '/esg-report',
          title: `ESG Report ${
            hasPermissions(permissions, ['ACCOUNT:ADMIN:SMARTFLOW']) &&
            devicesDueTo30DaysReport.length > 0
              ? `(${devicesDueTo30DaysReport.length})`
              : ''
          }`,
          icon: <FaWater />,
          permissions: ['READ:REPORTS:ESG'],
          defaultShow: false,
        },
        {
          href: '/water-usage',
          title: 'Water Usage',
          icon: <FaWater />,
          permissions: ['READ:USAGE'],
          defaultShow: false,
        },
        {
          href: '/historical-alerts',
          title: 'Historical Alerts',
          icon: <FaTriangleExclamation />,
          permissions: ['READ:ALERTS:USAGE:HISTORY'],
          defaultShow: false,
        },
        {
          href: '/invoice',
          title: 'Invoice',
          icon: <FaMoneyCheck />,
          permissions: ['READ:USAGE:INVOICE'],
          defaultShow: false,
          active: !!userInfo?.accounts?.find((account: UserAccountAssoc) => account.invoice),
        },
      ],
    },
    {
      heading: 'Control',
      menuItems: [
        {
          href: '/real-time-flow',
          title: 'Real-Time Flow',
          icon: <FaWater />,
          permissions: ['READ:DEVICE:RTF'],
          defaultShow: false,
        },
        {
          href: '/valves-management',
          title: `Operate Valves ${
            hasPermissions(permissions, ['DEVICE:VALVE:COMM']) && valveDevicesWithErrors.length > 0
              ? `(${valveDevicesWithErrors.length})`
              : ''
          }`,
          icon: <FaFaucet />,
          permissions: ['DEVICE:VALVE:PROD:OPERATE'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Support Tasks',
      menuItems: [
        {
          href: '/account-management?tab=locations_and_devices',
          title: 'Account Management',
          icon: <FaBuilding />,
          permissions: ['UPDATE:ACCOUNT||ACCOUNT:READ:SMARTFLOW'],
          defaultShow: false,
        },
        {
          href: '/account-management?tab=users_and_invitations',
          title: 'User Management',
          icon: <FaUsers />,
          permissions: ['UPDATE:DEVICE:PROD:USER'],
          defaultShow: false,
        },
        {
          href: '/device-management',
          title: 'Device Management',
          icon: <FaCog />,
          permissions: ['READ:DEVICE:PROD'],
          defaultShow: false,
        },
        {
          href: '/renewals',
          title: 'Customer Subscriptions',
          icon: <FaHistory />,
          permissions: ['READ:RENEWALS:ADMIN'],
          defaultShow: false,
        },
        {
          href: '/tickets',
          title: 'Device Tickets',
          icon: <FaScrewdriverWrench />,
          permissions: ['UPDATE:TICKET'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Test Devices',
      menuItems: [
        {
          href: '/test-bench',
          title: 'Test Bench',
          icon: <FaFlask />,
          permissions: ['READ:DEVICE:TEST', 'READ:DEVICE:STGD'],
          defaultShow: false,
        },
      ],
    },
    {
      heading: 'Utilities',
      menuItems: [
        {
          href: '/device-logger',
          title: 'Logger',
          icon: <FaHistory />,
          permissions: ['devices:logger:view'],
          defaultShow: false,
        },
      ],
    },
  ]

  return (
    <ScrollArea
      h={'100%'}
      type={'auto'}
      scrollbarSize={6}
      className={styles.root}
    >
      <Stack gap={0}>
        <Box
          p={'md'}
          display={'grid'}
          style={{
            gridTemplateColumns: 'minmax(0, 1fr) 1.75rem',
            alignItems: 'center',
          }}
        >
          <Center
            component={NavLink}
            to={'/home'}
            onClick={onNavigate}
            miw={0}
            style={{
              gridColumn: 1,
              justifyContent: 'flex-start',
            }}
          >
            <Image
              alt={'Smartflow'}
              src={'/assets/img/logo.svg'}
              w={'auto'}
              maw={'100%'}
              h={30}
              fit={'contain'}
            />
          </Center>
          <CloseButton
            onClick={onNavigate}
            c={'white'}
            aria-label={'Close sidebar'}
            style={{ gridColumn: 2 }}
          />
        </Box>
        <MantineNavLink
          component={NavLink}
          to={'/tutorials'}
          onClick={onNavigate}
          label={'Explore How To Videos'}
          leftSection={<FaHandsHelping />}
          className={`${styles.navLink} ${styles.tutorialLink}`}
        />
        {menu.map((menuCategory) => {
          const filteredItems = menuCategory.menuItems.filter((menuItem) =>
            menuItem.permissions.length > 0
              ? hasPermissions(permissions, menuItem.permissions) || menuItem.defaultShow
              : true,
          )
          return filteredItems.length > 0 ? (
            <SideBarMenuBlock
              key={menuCategory.heading}
              heading={menuCategory.heading}
              menuItems={filteredItems}
              onNavigate={onNavigate}
            />
          ) : null
        })}
      </Stack>
    </ScrollArea>
  )
}

export default SideBar
